import { Component, HostListener, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { ButtonModule, JfDialogDirectives } from '@adnova/jf-ng-components';
import { MatomoTracker } from '@ngx-matomo/tracker';

import { InfoPopupDialogActions } from './+store/info-popup-dialog.actions';
import { InfoPopupDialogSelectors } from './+store/info-popup-dialog.selectors';
import {
  UserAnnouncementEntitiesActions,
  UserAnnouncementEntitiesSelectors,
} from '../../../store/user-announcement-entities';

/**
 * Dialog-Komponente für das Info-Popup.
 *
 * Unterstützt zwei Modi:
 * - `'preview'`: Admin-Vorschau mit übergebenen PreviewData.
 * - `'user'`: Endnutzer-Popup mit Daten aus dem UserAnnouncementEntities-Store.
 */
@Component({
  selector: 'app-info-popup-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, JfDialogDirectives],
  templateUrl: './info-popup-dialog.component.html',
  styleUrls: ['./info-popup-dialog.component.scss'],
})
export class InfoPopupDialogComponent {
  private readonly _store = inject(Store);
  private readonly _tracker = inject(MatomoTracker);

  /** Aktueller Modus des Dialogs. */
  protected readonly mode$$ = this._store.selectSignal(InfoPopupDialogSelectors.mode);

  /** Vorschau-Daten (nur im Preview-Modus). */
  protected readonly previewData$$ = this._store.selectSignal(InfoPopupDialogSelectors.previewData);

  /** Aktuelles Announcement (nur im User-Modus). */
  protected readonly currentAnnouncement$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.currentAnnouncement);

  /** Blob-URL des Bildes des aktuellen Announcements (nur im User-Modus). */
  protected readonly currentImageUrl$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.currentAnnouncementImageUrl);

  /** Aktueller Navigationsindex. */
  protected readonly currentIndex$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.currentIndex);

  /** Gesamtanzahl der Announcements. */
  protected readonly totalCount$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.totalCount);

  /** Ob "Weiter >"-Button angezeigt werden soll. */
  protected readonly hasNext$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.hasNext);

  /** Ob "< Zurück"-Button angezeigt werden soll. */
  protected readonly hasPrev$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.hasPrev);

  /** Ob Navigation-Buttons angezeigt werden sollen (> 1 Announcement). */
  protected readonly navigationVisible$$ = this._store.selectSignal(UserAnnouncementEntitiesSelectors.navigationVisible);

  /** Trackt IMPRESSION-Event beim Öffnen im User-Modus. */
  private readonly _impressionEffect = effect(() => {
    if (this.mode$$() === 'user') {
      this._tracker.trackEvent('info-popup', 'impression');
    }
  });

  // --- Computed Properties ---

  /** Dialog-Header-Label je nach Modus. */
  protected get headerLabel(): string {
    return this.mode$$() === 'user' ? 'Neuigkeiten und Infos' : 'Vorschau';
  }

  /** Titel des aktuell angezeigten Eintrags. */
  protected get title(): string {
    if (this.mode$$() === 'user') {
      return this.currentAnnouncement$$()?.title ?? '';
    }
    return this.previewData$$()?.title ?? 'Kein Titel';
  }

  /** Beschreibungstext. */
  protected get content(): string {
    if (this.mode$$() === 'user') {
      return this.currentAnnouncement$$()?.content ?? '';
    }
    return this.previewData$$()?.content ?? 'Kein Inhalt';
  }

  /** Bild-URL (Blob-URL im User-Modus, direkte URL im Preview-Modus). */
  protected get imageUrl(): string | null | undefined {
    if (this.mode$$() === 'user') {
      return this.currentImageUrl$$();
    }
    return this.previewData$$()?.imageUrl;
  }

  /** Öffnet einen Link in neuem Tab, markiert als gelesen und navigiert weiter oder schließt den Dialog. */
  protected handleLinkClick(url: string): void {
    this._tracker.trackEvent('info-popup', 'cta-click', url);
    window.open(url, '_blank', 'noopener,noreferrer');
    this.markCurrentAsRead();

    if (this.hasNext$$()) {
      const currentId = this.currentAnnouncement$$()?.id;
      this._store.dispatch(UserAnnouncementEntitiesActions.navigate({ direction: 'next', currentId }));
    } else {
      this._store.dispatch(InfoPopupDialogActions.close());
    }
  }

  /** Öffnet den einzelnen Preview-Link. */
  protected handlePreviewLinkClick(): void {
    const url = this.previewData$$()?.linkUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /** Navigiert zum nächsten Eintrag. */
  protected handleNext(): void {
    this._tracker.trackEvent('info-popup', 'navigate-next');
    const currentId = this.currentAnnouncement$$()?.id;
    this._store.dispatch(UserAnnouncementEntitiesActions.navigate({ direction: 'next', currentId }));
  }

  /** Navigiert zum vorherigen Eintrag. */
  protected handlePrev(): void {
    this._tracker.trackEvent('info-popup', 'navigate-prev');
    const currentId = this.currentAnnouncement$$()?.id;
    this._store.dispatch(UserAnnouncementEntitiesActions.navigate({ direction: 'prev', currentId }));
  }

  /** Navigiert per Pfeiltasten zwischen Einträgen (nur im User-Modus). */
  @HostListener('keydown.arrowRight')
  protected handleArrowRight(): void {
    if (this.mode$$() === 'user' && this.hasNext$$()) {
      this.handleNext();
    }
  }

  /** Navigiert per Pfeiltasten zwischen Einträgen (nur im User-Modus). */
  @HostListener('keydown.arrowLeft')
  protected handleArrowLeft(): void {
    if (this.mode$$() === 'user' && this.hasPrev$$()) {
      this.handlePrev();
    }
  }

  /** Markiert das aktuell angezeigte Announcement als gelesen. */
  private markCurrentAsRead(): void {
    const id = this.currentAnnouncement$$()?.id;
    if (id) {
      this._store.dispatch(UserAnnouncementEntitiesActions.markRead({ id }));
    }
  }
}
